import { useState } from "react";
import { Loader2, Smartphone } from "lucide-react";
import { getCountryCallingCode, isPossiblePhoneNumber } from "libphonenumber-js";
import { api } from "@/lib/api";
import { COUNTRIES } from "@/constants/countries";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DIAL = COUNTRIES.flatMap((c) => {
  try { return [{ ...c, dial: getCountryCallingCode(c.code) }]; } catch { return []; }
});

export const ProfileCompletionModal = ({ open, onSaved, onDismiss }) => {
  const [country, setCountry] = useState("IN");
  const [number, setNumber] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!number.trim() || !isPossiblePhoneNumber(number, country)) return setError("Please enter a valid mobile number.");
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.put("/profile/mobile", { country_code: country, number });
      setNumber("");
      onSaved(data.mobile_e164);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Couldn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onDismiss()}>
      <DialogContent className="iv-glass max-w-[calc(100vw-2rem)] rounded-2xl border-white/10 bg-[#0B1021] sm:max-w-md" data-testid="mobile-modal">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2 text-white" data-testid="mobile-modal-title">
            <Smartphone className="h-4 w-4 text-cyan-300" aria-hidden="true" /> Complete your profile
          </DialogTitle>
          <DialogDescription className="text-slate-400">Add your mobile number to continue.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="min-w-0 space-y-3" noValidate>
          <label className="block text-xs font-semibold text-slate-300" htmlFor="mobile-number">Mobile number</label>
          <div className="flex min-w-0 gap-2">
            <Select value={country} onValueChange={(v) => { setCountry(v); setError(null); }}>
              <SelectTrigger className="h-10 w-[112px] shrink-0 rounded-xl border-white/10 bg-white/[0.04] text-sm text-white" aria-label="Country code" data-testid="mobile-country-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72 max-w-[calc(100vw-2rem)] border-white/10 bg-[#0B1021] text-white">
                {DIAL.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`mobile-country-option-${c.code}`}>{c.flag} {c.code} +{c.dial}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input id="mobile-number" type="tel" size={1} inputMode="tel" autoComplete="tel-national" value={number} maxLength={20}
              onChange={(e) => { setNumber(e.target.value.replace(/[^\d\s()+-]/g, "")); setError(null); }}
              placeholder="98765 43210" data-testid="mobile-number-input"
              className="h-10 min-w-0 flex-1 rounded-xl border border-white/10 bg-white/[0.04] px-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/50" />
          </div>
          {error && <p className="text-xs text-rose-300" role="alert" data-testid="mobile-error">{error}</p>}
          <button type="submit" disabled={busy} className="iv-cta inline-flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold text-white disabled:opacity-60" data-testid="mobile-save-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Save & continue
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
